import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const BundleForm = ({ bundle = null, onClose, onSave }) => {
  const isEdit = !!bundle;
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    bundle_sku: '',
    bundle_image: '',
    category_id: '',
    bundle_type: 'fixed',
    customer_type: 'b2c',
    bundle_price: '',
    is_featured: false,
    track_quantity: false,
    stock_quantity: 0,
    valid_from: '',
    valid_until: '',
    tags: []
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(bundle?.bundle_image || '');

  // Load initial data
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    
    if (bundle) {
      setFormData({
        title: bundle.title || '',
        title_en: bundle.title_en || '',
        description: bundle.description || '',
        description_en: bundle.description_en || '',
        bundle_sku: bundle.bundle_sku || '',
        bundle_image: bundle.bundle_image || '',
        category_id: bundle.category_id || '',
        bundle_type: bundle.bundle_type || 'fixed',
        customer_type: bundle.customer_type || 'b2c',
        bundle_price: bundle.bundle_price || '',
        is_featured: bundle.is_featured || false,
        track_quantity: bundle.track_quantity || false,
        stock_quantity: bundle.stock_quantity || 0,
        valid_from: bundle.valid_from ? bundle.valid_from.split('T')[0] : '',
        valid_until: bundle.valid_until ? bundle.valid_until.split('T')[0] : '',
        tags: bundle.tags || []
      });
      
      setImagePreview(bundle.bundle_image || '');
      
      if (bundle.items) {
        setSelectedProducts(
          bundle.items.map(item => ({
            ...item.product,
            quantity: item.quantity,
            sort_order: item.sort_order
          }))
        );
      }
    }
  }, [bundle]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/products?limit=1000', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.allCategories || response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('נא להעלות קובץ תמונה (JPEG, PNG, או WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('גודל התמונה חייב להיות פחות מ-5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError('');

      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      
      const response = await axios.post('/api/products/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, bundle_image: imageUrl }));
      setImagePreview(imageUrl);
    } catch (error) {
      setError(error.response?.data?.message || 'שגיאה בהעלאת התמונה');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, bundle_image: '' }));
    setImagePreview('');
  };

  // Load products and categories when component mounts
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Calculate regular price from selected products
  const calculateRegularPrice = () => {
    return selectedProducts.reduce((sum, product) => {
      return sum + (parseFloat(product.price_b2c) * (product.quantity || 1));
    }, 0);
  };

  // Calculate discount percentage
  const calculateDiscount = () => {
    const regular = calculateRegularPrice();
    const bundle = parseFloat(formData.bundle_price) || 0;
    if (regular === 0) return 0;
    return ((regular - bundle) / regular * 100).toFixed(2);
  };

  const handleAddProduct = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) {
      alert('המוצר כבר נמצא בחבילה');
      return;
    }
    
    setSelectedProducts([
      ...selectedProducts,
      { ...product, quantity: 1, sort_order: selectedProducts.length }
    ]);
    setShowProductSearch(false);
    setProductSearch('');
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleProductQuantityChange = (productId, quantity) => {
    setSelectedProducts(
      selectedProducts.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(quantity) || 1 } : p
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.title) {
      setError('נא למלא שם חבילה');
      return;
    }

    if (!formData.bundle_sku) {
      setError('נא למלא מק"ט חבילה');
      return;
    }

    if (selectedProducts.length < 2) {
      setError('חבילה חייבת להכיל לפחות 2 מוצרים');
      return;
    }

    const regularPrice = calculateRegularPrice();
    const bundlePrice = parseFloat(formData.bundle_price);

    if (!bundlePrice || bundlePrice <= 0) {
      setError('נא להזין מחיר חבילה תקין');
      return;
    }

    if (bundlePrice >= regularPrice) {
      setError('מחיר החבילה חייב להיות נמוך ממחיר רגיל');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        // Convert empty date strings to null
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        regular_price: regularPrice,
        items: selectedProducts.map((p, index) => ({
          product_id: p.id,
          quantity: p.quantity || 1,
          is_required: true,
          sort_order: index
        }))
      };

      let response;
      if (isEdit) {
        response = await axios.put(`/api/bundles/${bundle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post('/api/bundles', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave(response.data);
      onClose();
    } catch (error) {
      setError(error.response?.data?.error || 'שגיאה בשמירת החבילה');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products
    .filter(p =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(productSearch.toLowerCase())
    )
    .slice(0, 50); // Limit to 50 results for performance

  const regularPrice = calculateRegularPrice();
  const discount = calculateDiscount();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'עריכת חבילה' : 'יצירת חבילה חדשה'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">פרטים בסיסיים</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם חבילה <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="חבילת טיפוח מושלמת"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם באנגלית
                </label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Perfect Care Bundle"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מק"ט חבילה <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.bundle_sku}
                  onChange={(e) => setFormData({ ...formData, bundle_sku: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="BUNDLE-001"
                  disabled={isEdit}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  קטגוריה
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">בחר קטגוריה</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סוג לקוח <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.customer_type}
                  onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="b2c">B2C - לקוחות פרטיים</option>
                  <option value="b2b">B2B - לקוחות עסקיים</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  החבילה תהיה זמינה רק לסוג הלקוח שנבחר
                </p>
              </div>
            </div>

            {/* Image Upload */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תמונת חבילה
              </label>
              
              <div className="flex items-start gap-4">
                {/* Image Preview */}
                {imagePreview && (
                  <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden">
                    <img
                      src={imagePreview.startsWith('http') ? imagePreview : `/api${imagePreview}`}
                      alt="Bundle preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="flex-1">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploadingImage ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                      ) : (
                        <>
                          <PhotoIcon className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">
                            <span className="font-semibold">לחץ להעלאת תמונה</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP עד 5MB</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תיאור
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="תיאור מפורט של החבילה..."
              />
            </div>
          </div>

          {/* Products Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">
              מוצרים בחבילה <span className="text-red-500">*</span>
            </h3>

            {/* Selected Products */}
            {selectedProducts.length > 0 && (
              <div className="mb-4 space-y-2">
                {selectedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                  >
                    <img
                      src={product.images?.[0] ? `/api${product.images[0]}` : 'https://via.placeholder.com/50'}
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-600">₪{product.price_b2c}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">כמות:</label>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity || 1}
                        onChange={(e) => handleProductQuantityChange(product.id, e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>

                    <div className="text-sm font-medium text-gray-900">
                      ₪{(parseFloat(product.price_b2c) * (product.quantity || 1)).toFixed(2)}
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveProduct(product.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Product Button */}
            <button
              type="button"
              onClick={() => setShowProductSearch(!showProductSearch)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100"
            >
              <PlusIcon className="h-5 w-5" />
              הוסף מוצר
            </button>

            {/* Product Search */}
            {showProductSearch && (
              <div className="mt-4 border border-gray-300 rounded-lg p-4">
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="חפש מוצר..."
                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="max-h-60 overflow-y-auto space-y-2">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      {products.length === 0 ? 'טוען מוצרים...' : 'לא נמצאו מוצרים'}
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleAddProduct(product)}
                        className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-right"
                        disabled={selectedProducts.find(p => p.id === product.id)}
                      >
                        <img
                          src={product.images?.[0] ? `/api${product.images[0]}` : 'https://via.placeholder.com/40'}
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-600">₪{product.price_b2c}</div>
                        </div>
                        {selectedProducts.find(p => p.id === product.id) && (
                          <span className="text-sm text-green-600">✓ נבחר</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">תמחור</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">מחיר רגיל</div>
                <div className="text-2xl font-bold text-gray-900">
                  ₪{regularPrice.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">סכום כל המוצרים</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מחיר חבילה <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.bundle_price}
                  onChange={(e) => setFormData({ ...formData, bundle_price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="0.00"
                />
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-sm text-green-600 mb-1">הנחה</div>
                <div className="text-2xl font-bold text-green-600">
                  {discount}%
                </div>
                <div className="text-xs text-green-600 mt-1">
                  חיסכון: ₪{(regularPrice - parseFloat(formData.bundle_price || 0)).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">אפשרויות</h3>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-gray-700">חבילה מומלצת (יוצג בדף הבית)</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.track_quantity}
                  onChange={(e) => setFormData({ ...formData, track_quantity: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-gray-700">מעקב אחר מלאי חבילה</span>
              </label>

              {formData.track_quantity && (
                <div className="mr-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    כמות במלאי
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                    className="w-32 px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    תוקף מתאריך
                  </label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    תוקף עד תאריך
                  </label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'שומר...' : isEdit ? 'עדכן חבילה' : 'צור חבילה'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BundleForm;
